import { useStore, selectInventory } from '@/infra/store';
import { useT } from '@/infra/i18n';
import { COLORS, SPACING, RADIUS, FONT } from '@/presentation/theme/tokens';
import { ALL_ITEM_TYPES, type ItemType } from '@/domain/world/types';
import { overlayStyle, cardStyle } from '@/presentation/modals/modal-shared';
import { PixelIcon, type PixelIconName } from '@/presentation/icons/PixelIcon';

interface Props {
  open: boolean;
  onClose: () => void;
}

const ITEM_ICON: Record<ItemType, PixelIconName> = {
  berry: 'berry',
  shell: 'shell',
  crystal: 'crystal',
  flower: 'flower',
};

export function InventoryModal({ open, onClose }: Props) {
  const t = useT();
  const inv = useStore(selectInventory);
  if (!open) return null;
  const total = ALL_ITEM_TYPES.reduce((acc, itemType) => acc + inv[itemType], 0);
  return (
    <div role="dialog" data-testid="inventory-modal" style={overlayStyle} onClick={onClose}>
      <div style={{ ...cardStyle, maxWidth: 320 }} onClick={(e) => e.stopPropagation()}>
        <h2 style={titleStyle}>
          <PixelIcon name="inventory" size={28} color="#ffd6b3" />
          {t('inventory.title')}
        </h2>
        {total === 0 && (
          <p style={emptyStyle}>{t('inventory.empty')}</p>
        )}
        <div style={gridStyle}>
          {ALL_ITEM_TYPES.map((type) => (
            <div key={type} data-testid={`inv-${type}`} style={cellStyle(inv[type] > 0)}>
              <PixelIcon name={ITEM_ICON[type]} size={32} color="#ffd6b3" />
              <span style={{ fontSize: FONT.sizeSm, color: COLORS.text }}>{t(`item.${type}`)}</span>
              <span style={{ fontFamily: FONT.mono, fontSize: FONT.sizeXs, color: COLORS.textDim }}>x{inv[type]}</span>
            </div>
          ))}
        </div>
        <button
          data-testid="inv-close"
          onClick={onClose}
          style={closeBtn}
        >
          {t('settings.close')}
        </button>
      </div>
    </div>
  );
}

const titleStyle: React.CSSProperties = {
  margin: `0 0 ${SPACING.md}px 0`,
  color: COLORS.accent,
  fontSize: FONT.sizeLg,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: SPACING.xs,
};
const emptyStyle: React.CSSProperties = {
  color: COLORS.textDim,
  textAlign: 'center',
  fontSize: FONT.sizeSm,
  marginBottom: SPACING.md,
};
const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: SPACING.sm,
  marginBottom: SPACING.lg,
};
function cellStyle(has: boolean): React.CSSProperties {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    padding: SPACING.sm,
    background: has ? COLORS.surfaceAlt : 'rgba(40,40,50,0.5)',
    borderRadius: RADIUS.md,
    border: `1px solid ${has ? COLORS.accent : COLORS.border}`,
    opacity: has ? 1 : 0.5,
  };
}
const closeBtn: React.CSSProperties = {
  background: 'transparent',
  color: COLORS.textDim,
  border: `1px solid ${COLORS.border}`,
  borderRadius: RADIUS.md,
  padding: '6px 14px',
  cursor: 'pointer',
  fontSize: FONT.sizeSm,
  width: '100%',
};
